import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import { apiErrorMessage } from '../api/growth.api';
import { useEligibleBookings, useUploadRewardPhoto } from '../hooks/useGrowth';
import type { PhotoType } from '../types';
import { useRewardsToast } from './RewardsToast';

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'];

interface PhotoUploadDialogProps {
  type: PhotoType | null;
  onClose: () => void;
}

export function PhotoUploadDialog({ type, onClose }: PhotoUploadDialogProps) {
  const { t } = useTranslation('rewards');
  const notify = useRewardsToast();
  const fullScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const traveller = type === 'TRAVELLER_PHOTO';
  const bookings = useEligibleBookings(traveller);
  const upload = useUploadRewardPhoto();
  const [file, setFile] = useState<File | null>(null);
  const [bookingId, setBookingId] = useState('');
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState('');

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!type) {
      setFile(null);
      setBookingId('');
      setConsent(false);
      setError('');
      upload.reset();
    }
    // reset only when the dialog closes
  }, [type]);

  const pick = (selected?: File) => {
    setError('');
    if (!selected) return;
    if (!ACCEPTED.includes(selected.type)) return setError(t('photo.fileType'));
    if (selected.size > MAX_BYTES) return setError(t('photo.fileTooBig'));
    setFile(selected);
  };

  const submit = () => {
    if (!file || !type) return;
    if (traveller && !consent) return setError(t('photo.consentRequired'));
    upload.mutate(
      { file, type, bookingId: traveller ? bookingId : undefined, consent: traveller ? consent : false },
      {
        onSuccess: () => {
          notify(t('photo.success'));
          onClose();
        },
        onError: (err) => setError(apiErrorMessage(err, t('common.error'))),
      }
    );
  };

  const noBookings = traveller && bookings.isSuccess && bookings.data.length === 0;
  const canSubmit = Boolean(file) && (!traveller || (bookingId && consent)) && !upload.isPending;

  return (
    <Dialog open={Boolean(type)} onClose={upload.isPending ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 800 }}>{traveller ? t('photo.travellerTitle') : t('photo.selfieTitle')}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          {traveller ? t('photo.travellerHint') : t('photo.selfieHint')}
        </Typography>

        {traveller && (
          noBookings ? (
            <Alert severity="info">{t('photo.noBookings')}</Alert>
          ) : (
            <TextField
              select
              label={t('photo.booking')}
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              disabled={bookings.isLoading}
              fullWidth
            >
              {(bookings.data ?? []).map((booking) => (
                <MenuItem key={booking.id} value={booking.id}>
                  {booking.label ? `${booking.label} · ` : ''}
                  {t('photo.tripLabel', { date: dayjs(booking.startAt ?? booking.completedAt).format('D MMM YYYY') })}
                </MenuItem>
              ))}
            </TextField>
          )
        )}

        <Box
          component="label"
          sx={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 200,
            borderRadius: 3,
            border: '2px dashed',
            borderColor: file ? 'transparent' : 'divider',
            bgcolor: 'background.default',
            cursor: 'pointer',
            overflow: 'hidden',
            '&:focus-within': { outline: '2px solid', outlineColor: 'primary.main' },
          }}
        >
          <input
            type="file"
            accept={ACCEPTED.join(',')}
            hidden
            onChange={(e) => pick(e.target.files?.[0])}
          />
          {previewUrl ? (
            <Box component="img" src={previewUrl} alt="" sx={{ width: '100%', maxHeight: 320, objectFit: 'contain' }} />
          ) : (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', p: 3 }}>
              <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 40 }} />
              <Typography fontWeight={700}>{t('photo.choose')}</Typography>
              <Typography variant="caption">JPG · PNG · WebP · 10 MB</Typography>
            </Box>
          )}
        </Box>
        {file && (
          <Button component="label" size="small" sx={{ justifySelf: 'start' }}>
            {t('photo.change')}
            <input type="file" accept={ACCEPTED.join(',')} hidden onChange={(e) => pick(e.target.files?.[0])} />
          </Button>
        )}

        {traveller && (
          <FormControlLabel
            control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} />}
            label={<Typography variant="body2">{t('photo.consent')}</Typography>}
            sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
          />
        )}

        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={upload.isPending}>
          {t('photo.cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit} sx={{ borderRadius: 999, fontWeight: 700 }}>
          {upload.isPending ? t('photo.uploading') : t('photo.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
